import { Injectable } from '@angular/core';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { determineIsMobile, filterNullUndefined } from '@domgen/dgx-fe-common';
import { GAService } from '@domgen/dgx-fe-ga';
import { ComponentStore } from '@ngrx/component-store';
import {
  getConfirmationRegistrationApplianceDetails,
  RegistrationFacade,
  RegistrationService,
} from '@us-registration/data-access';

import {
  CmsRegistrationConfirmationPage,
  CustomerDetails,
  RegisteredItem,
  RegisteredItemProductDetails,
  RegistrationApplianceDetails,
  RegistrationCPGRequest,
  RegistrationCPGResponse,
  RegistrationRequest,
  RegistriaUserResponse,
} from '@us-registration/util-models';
import {
  combineLatest,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

export interface ViewModel {
  cmsData: CmsRegistrationConfirmationPage;
  customerDetails?: CustomerDetails;
  isMobile: boolean;
  registeredAppliances?: RegistrationApplianceDetails[];
  registrationCPG?: RegistrationCPGResponse;
  registriaUserResponse?: RegistriaUserResponse;

  showRegistrationCPG: boolean;
}

export interface ComponentState {
  customerDetails?: CustomerDetails;
  registeredAppliances?: RegistrationApplianceDetails[];
  registrationCPG?: RegistrationCPGResponse;
  registrationCPGRequest?: RegistrationCPGRequest;
  registriaUserResponse?: RegistriaUserResponse;
  showRegistrationCPG: boolean;
  showRegistriaSnippet: boolean;
}

@Injectable()
export class RegistrationConfirmationComponentService extends ComponentStore<ComponentState> {
  private readonly registeredAppliances$: Observable<
    RegistrationApplianceDetails[] | undefined
  > = this.select((state) => state.registeredAppliances);

  private readonly registriaUserResponse$: Observable<
    RegistriaUserResponse | undefined
  > = this.select((state) => state.registriaUserResponse);

  private readonly registrationCPGRequest$: Observable<
    RegistrationCPGRequest | undefined
  > = this.select((state: ComponentState) => state.registrationCPGRequest);

  private readonly registrationCPG$: Observable<
    RegistrationCPGResponse | undefined
  > = this.select((state: ComponentState) => state.registrationCPG);

  private readonly showRegistrationCPG$: Observable<boolean> = this.select(
    (state: ComponentState) => state.showRegistrationCPG
  );

  private readonly showRegistriaSnippet$: Observable<boolean> = this.select(
    (state: ComponentState) => state.showRegistriaSnippet
  );

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    customerDetails: this.registrationFacade.registrationCustomerDetails$,
    isMobile: determineIsMobile(),
    registeredAppliances: this.registeredAppliances$,
    registriaUserResponse: this.registriaUserResponse$,
    showRegistriaSnippet: this.showRegistriaSnippet$,
    registrationCPG: this.registrationCPG$,
    showRegistrationCPG: this.showRegistrationCPG$,
  });

  constructor(
    private readonly cmsContainer: CmsContainer<CmsRegistrationConfirmationPage>,
    private readonly registrationFacade: RegistrationFacade,
    private readonly registrationService: RegistrationService,
    private readonly gaService: GAService
  ) {
    super({
      registeredAppliances: [],
      showRegistrationCPG: false,
      showRegistriaSnippet: false,
    });
  }

  private readonly updateRegistriaUserResponse = this.updater(
    (
      state: ComponentState,
      registriaUserResponse: RegistriaUserResponse | undefined
    ) => ({
      ...state,
      registriaUserResponse,
    })
  )(this.registrationFacade.registriaUserResponse$);

  private readonly updateRegisteredAppliances = this.updater(
    (
      state: ComponentState,
      registeredAppliances?: RegistrationApplianceDetails[]
    ) => ({
      ...state,
      registeredAppliances,
    })
  )(
    combineLatest([
      this.registrationFacade.registrationRequest$,
      this.registrationFacade.serializationCodeResponse$,
    ]).pipe(
      map(([registrationRequest, serializationCodeResponse]) => {
        if (!registrationRequest) {
          return [];
        }
        return getConfirmationRegistrationApplianceDetails(
          registrationRequest.merchandise,
          serializationCodeResponse
        );
      })
    )
  );

  updateRegistrationCPG = this.updater(
    (state: ComponentState, registrationCPG?: RegistrationCPGResponse) => ({
      ...state,
      registrationCPG,
    })
  );

  readonly updateShowRegistrationCPG = this.updater(
    (state: ComponentState, showRegistrationCPG: boolean) => ({
      ...state,
      showRegistrationCPG,
    })
  )(
    this.registrationCPG$.pipe(
      map((registrationCPG) => typeof registrationCPG !== 'string')
    )
  );

  readonly updateRegistrationCPGRequest = this.updater(
    (
      state: ComponentState,
      registrationCPGRequest: RegistrationCPGRequest | undefined
    ) => ({
      ...state,
      registrationCPGRequest,
    })
  )(
    this.registrationFacade.registrationRequest$.pipe(
      filterNullUndefined(),
      map((registrationRequest) => {
        if (!registrationRequest.merchandise[0].modelNumber) {
          return undefined;
        }

        return {
          modelNumber: registrationRequest.merchandise[0].modelNumber,
        };
      })
    )
  );

  private readonly getRegistrationCPGOffer: Subscription = this.effect(
    (trigger$: Observable<RegistrationCPGRequest | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        switchMap((registrationCPGRequest) =>
          this.registrationService.getApplianceCPG(registrationCPGRequest)
        ),
        tap((registrationCPGResponse) =>
          this.updateRegistrationCPG(
            registrationCPGResponse as RegistrationCPGResponse
          )
        )
      )
  )(this.registrationCPGRequest$);

  private readonly trackConfirmationGAPageViewWithQuote: Subscription =
    this.effect((trigger$: Observable<RegisteredItem | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        tap((registeredItem: RegisteredItem) => {
          if (registeredItem.appliances && registeredItem.products) {
            const appliance: RegistrationApplianceDetails =
              registeredItem.appliances[0];
            const product: RegisteredItemProductDetails =
              registeredItem.products[0];

            this.gaService.pageView({
              pagename: 'confirmation',
              applianceName: appliance.type,
              appliancePurchaseCost: appliance.purchasePrice,
              appliancePurchaseDate: appliance.purchaseDate,
              offerGeneratedPrice: product.price,
            });
          }
        })
      )
    )(this.registrationFacade.registeredItem$);

  private readonly trackConfirmationGAPageViewWithoutQuote: Subscription =
    this.effect(
      (
        trigger$: Observable<
          [RegistrationRequest | undefined, RegisteredItem | undefined]
        >
      ) =>
        trigger$.pipe(
          filterNullUndefined(),
          tap(([registrationRequest, registeredItem]) => {
            if (registrationRequest && !registeredItem?.products) {
              const appliance: RegistrationApplianceDetails =
                registrationRequest.merchandise[0];

              this.gaService.pageView({
                pagename: 'confirmation',
                applianceName: appliance.type,
                appliancePurchaseCost: appliance.purchasePrice,
                appliancePurchaseDate: appliance.purchaseDate,
                offerGeneratedPrice: 0,
              });
            }
          })
        )
    )(
      combineLatest([
        this.registrationFacade.registrationRequest$,
        this.registrationFacade.registeredItem$,
      ])
    );
}
